
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mountain, Users, LayoutGrid, CheckCircle2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

type AccountType = 'consultant' | 'client' | null;

export const SignupPage: React.FC = () => {
  const [accountType, setAccountType] = useState<AccountType>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const roleParam = searchParams.get('role');
    if (roleParam === 'consultant') {
        setAccountType('consultant');
    } else if (roleParam === 'client') {
        setAccountType('client');
    }
  }, [searchParams]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountType) return;
    
    setError('');
    setIsSubmitting(true);

    try {
      await signup(email, password, accountType);
      // Redirect to the appropriate onboarding flow, passing the plan if present
      const plan = searchParams.get('plan');
      if (accountType === 'consultant') {
          navigate('/onboarding/consultancy', { state: { plan } });
      } else {
          navigate('/onboarding/client');
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to create account');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mb-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2 justify-center">
             <div className="bg-blue-900 p-1.5 rounded-lg">
                <Mountain className="h-6 w-6 text-white" />
             </div>
             <span className="font-bold text-xl text-slate-900 tracking-tight">RegulatorySherpas</span>
          </Link>
      </div>

      <div className="max-w-3xl w-full bg-white p-8 rounded-xl shadow-sm border border-slate-200">
        {!accountType ? (
            // Step 1: Select Account Type
            <div className="text-center">
                <h2 className="text-3xl font-extrabold text-slate-900 mb-4">Join the global regulatory network</h2>
                <p className="text-slate-600 mb-10">Select your account type to get started</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <button 
                        onClick={() => setAccountType('client')}
                        className="p-8 border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group text-left relative"
                    >
                        <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                            <Users className="w-6 h-6 text-blue-700" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2">Pharma / Biotech</h3>
                        <p className="text-slate-500 text-sm">
                            I am looking for regulatory consultants, dossier writers, or strategic advisors.
                        </p>
                    </button>

                    <button 
                        onClick={() => setAccountType('consultant')}
                        className="p-8 border-2 border-slate-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all group text-left relative"
                    >
                        <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
                            <LayoutGrid className="w-6 h-6 text-purple-700" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2">Consultancy</h3>
                        <p className="text-slate-500 text-sm">
                            I am a regulatory consultant or agency wanting to list services and find clients.
                        </p>
                    </button>
                </div>
                
                <div className="mt-8">
                    <p className="text-sm text-slate-500">
                        Already have an account? <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">Sign in</Link>
                    </p>
                </div>
            </div>
        ) : (
            // Step 2: Create Account
            <div className="max-w-md mx-auto">
                <button 
                    onClick={() => setAccountType(null)}
                    className="text-sm text-slate-500 hover:text-slate-800 mb-6"
                >
                    ← Back to account selection
                </button>
                
                <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-slate-900">
                        Create {accountType === 'client' ? 'Client' : 'Consultancy'} Account
                    </h2>
                    <p className="text-slate-600 mt-2">Enter your details to proceed to onboarding.</p>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 flex items-center gap-2 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                    </div>
                )}

                <form onSubmit={handleSignup} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Work Email</label>
                        <input 
                            type="email" 
                            required 
                            className="w-full px-4 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                        <input 
                            type="password" 
                            required 
                            minLength={8}
                            className="w-full px-4 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </div>
                    
                    <div className="bg-slate-50 p-4 rounded-md text-sm text-slate-600">
                        <ul className="space-y-2">
                            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500"/> Access global regulatory directory</li>
                            {accountType === 'consultant' && (
                                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500"/> Showcase your track record</li>
                            )}
                            {accountType === 'client' && (
                                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500"/> View verified approval data</li>
                            )}
                        </ul>
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-3 px-4 border border-transparent text-base font-medium rounded-md text-white bg-blue-900 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-70"
                    >
                        {isSubmitting ? 'Creating Account...' : 'Create Account'}
                    </button>
                </form>
            </div>
        )}
      </div>
    </div>
  );
};
